import { useEffect, useMemo, useRef, useState } from "react";
import { allIanaTimeZones } from "../datetime";

const LIST_MAX = 12;

function normalizeQuery(q: string): string {
  return q.trim().toLowerCase();
}

export default function TimezoneSelect({
  id,
  value,
  onChange,
  disabled,
}: {
  id: string;
  /** Selected IANA zone, or null for “use IP-based”. */
  value: string | null;
  onChange: (iana: string | null) => void;
  disabled?: boolean;
}) {
  const zones = useMemo(() => allIanaTimeZones(), []);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const n = normalizeQuery(query);
    if (!n) return zones.slice(0, LIST_MAX);
    const hits: string[] = [];
    for (const z of zones) {
      if (z.toLowerCase().includes(n)) {
        hits.push(z);
        if (hits.length >= LIST_MAX) break;
      }
    }
    return hits;
  }, [zones, query]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const el = rootRef.current;
      if (el && !el.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const displayValue = value ?? "";

  return (
    <div ref={rootRef} className="timezone-select">
      <div className="timezone-select__field">
        <input
          id={id}
          type="text"
          className="timezone-select__input"
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
          placeholder={value ? value : "Search IANA timezones…"}
          value={open ? query : displayValue}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
            setQuery(displayValue);
          }}
          aria-expanded={open}
          aria-controls={`${id}-listbox`}
          aria-autocomplete="list"
          role="combobox"
        />
        {open ? (
          <ul
            id={`${id}-listbox`}
            className="timezone-select__list"
            role="listbox"
          >
            {filtered.length === 0 ? (
              <li className="timezone-select__empty muted">No matches</li>
            ) : (
              filtered.map((z) => (
                <li key={z} role="option">
                  <button
                    type="button"
                    className="timezone-select__option"
                    onClick={() => {
                      onChange(z);
                      setQuery("");
                      setOpen(false);
                    }}
                  >
                    {z}
                  </button>
                </li>
              ))
            )}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
